import type { ObjectToCamel } from "ts-case-convert";

import { createError } from "#app";
import { isObject, shake } from "radash";
import { objectToCamel } from "ts-case-convert";
import { reactive, toValue } from "vue";

import type { LengthAwarePaginator, ToMaybeRefs } from "@solvari/utils";

import {
  isError,
  networkErrorToSentry,
  NotFoundError,
  returnNetworkError,
  throwError,
  toSearchParams,
} from "@solvari/utils";

import { ziggy } from "@/shared/libs/ziggy";
import {
  kyCacheTags,
  setPageUncacheable,
} from "~/shared/composables/useCacheTags.ts";
import { handleNuxtQuery } from "~/shared/libs/networkErrorHandling.ts";

class RecentlyDeletedCustomerError extends Error {
  constructor() {
    super("Customer was recently deleted");
    this.name = "RecentlyDeletedCustomerError";
  }
}

function useCompanyWallData(
  params: ToMaybeRefs<App.Http.Requests.Api.V3.Customer.CustomerCompanyWallRequest>,
) {
  return {
    key: () => ["api.v3.main-site.customer-wall", reactive(params)],
    query: throwError(async () => {
      const result = await returnNetworkError(async () =>
        objectToCamel(
          await kyCacheTags
            .get(ziggy("api.v3.main-site.customer-wall"), {
              searchParams: shake(reactive(params)),
              // eslint-disable-next-line @typescript-eslint/naming-convention
              headers: { "solvari-locale": toValue(params.localeCode) },
            })
            .json<App.Data.CompanyWallData>(),
        ),
      )();

      if (result instanceof NotFoundError) {
        const body: unknown = await result.response.clone().json();

        const reason = isObject(body)
          ? (body as { data?: { reason?: string } }).data?.reason
          : undefined;

        if (reason === "recently_deleted") {
          return new RecentlyDeletedCustomerError();
        }

        return createError({
          statusCode: 404,
          statusMessage: "Page Not Found",
        });
      }

      if (isError(result)) {
        networkErrorToSentry(result);
        setPageUncacheable();
      }

      return result;
    }),
    staleTime: Infinity,
    ssrCatchError: true,
  };
}

function useCompanyWallReviews(
  params: ToMaybeRefs<{
    customerId: number;
    locale: Solvari.Common.Enums.LocaleEnum;
    page?: number;
  }>,
) {
  return {
    key: () => ["company-wall", "reviews", reactive(params)],
    query: handleNuxtQuery(async () =>
      objectToCamel(
        await kyCacheTags
          .get(
            ziggy("api.v3.reviews.customer_reviews", {
              customerId: toValue(params.customerId),
            }),
            {
              searchParams: toSearchParams({
                page: toValue(params.page),
                locale: toValue(params.locale),
              }),
              // eslint-disable-next-line @typescript-eslint/naming-convention
              headers: { "solvari-locale": toValue(params.locale) },
            },
          )
          .json<
            ObjectToCamel<
              LengthAwarePaginator<App.DTO.Customer.Review.CustomerReviewDTO>
            >
          >(),
      ),
    ),
    staleTime: 10 * 60 * 1000,
  };
}

export {
  RecentlyDeletedCustomerError,
  useCompanyWallData,
  useCompanyWallReviews,
};
